import { OrderItemCreate } from "@/api";
import { Button } from "@/framework/buttons";
import FormCard from "@/framework/forms/FormCard";
import FormInput from "@/framework/forms/FormInput";
import FormSelect from "@/framework/forms/FormSelect";
import { useState } from "react";


interface ApplicationOption {
  value: number;
  label: string;
}

interface OrderItemsCreateProps {
  items: OrderItemCreate[];
  applicationOptions: ApplicationOption[];
  onChange: (items: OrderItemCreate[]) => void;
}

export function OrderItemsCreate({
  items,
  applicationOptions,
  onChange,
}: OrderItemsCreateProps) {
  const [applicationId, setApplicationId] =
    useState<number | "">("");

  const [quantity, setQuantity] =
    useState<number | "">(1);

  const [editingIndex, setEditingIndex] =
    useState<number | null>(null);

  const [error, setError] =
    useState<string | null>(null);

  const isEditing =
    editingIndex !== null;

  const resetEditor = () => {
    setApplicationId("");
    setQuantity(1);
    setEditingIndex(null);
    setError(null);
  };

  const handleAdd = () => {
    setError(null);

    if (!applicationId) {
      setError(
        "Please select an application.",
      );
      return;
    }

    if (!quantity || quantity <= 0) {
      setError(
        "Quantity must be greater than zero.",
      );
      return;
    }

    const newItem: OrderItemCreate = {
      application_id: Number(applicationId),
      quantity: Number(quantity),
    };

    onChange([
      ...items,
      newItem,
    ]);

    resetEditor();
  };

  const handleEdit = (
    index: number,
  ) => {
    const item = items[index];

    if (!item) {
      return;
    }

    setApplicationId(
      item.application_id,
    );

    setQuantity(
      item.quantity,
    );

    setEditingIndex(index);
    setError(null);
  };

  const handleUpdate = () => {
    setError(null);

    if (!applicationId) {
      setError(
        "Please select an application.",
      );
      return;
    }

    if (!quantity || quantity <= 0) {
      setError(
        "Quantity must be greater than zero.",
      );
      return;
    }

    if (editingIndex === null) {
      return;
    }

    const updatedItems = [...items];

    updatedItems[editingIndex] = {
      application_id:
        Number(applicationId),

      quantity:
        Number(quantity),
    };

    onChange(updatedItems);

    resetEditor();
  };

  const handleDelete = (
    index: number,
  ) => {
    const updatedItems =
      items.filter(
        (_, itemIndex) =>
          itemIndex !== index,
      );

    onChange(updatedItems);

    if (editingIndex === index) {
      resetEditor();
    }
  };

  const applicationLabel = (
    id: number,
  ) => {
    return (
      applicationOptions.find(
        (option) =>
          Number(option.value) === id,
      )?.label ??
      `Application #${id}`
    );
  };

  return (
    <FormCard
      title="Order Items"
      subtitle="Select the applications required for this order."
    >

      {/* =====================================================
          ITEM ENTRY ROW
      ===================================================== */}

      <div className="rounded-lg border p-4">

        <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-4">

          {/* Application */}

          <div className="md:col-span-2">

            <FormSelect
              label="Application"
              value={applicationId}
              required
              placeholder="Select application"
              options={applicationOptions}
              onChange={(value) => {
                setApplicationId(
                  value === ""
                    ? ""
                    : Number(value),
                );
              }}
            />

          </div>

          {/* Quantity */}

          <div>

            <FormInput
              label="Quantity"
              value={quantity}
              required
              validation="numeric"
              placeholder="Quantity"
              onValueChange={(value) => {
                setQuantity(
                  value === ""
                    ? ""
                    : Number(value),
                );
              }}
            />

          </div>

          {/* Add / Update */}

          <div className="flex gap-2">

            {isEditing && (
              <Button
                type="button"
                variant="secondary"
                onClick={resetEditor}
              >
                Cancel
              </Button>
            )}

            <Button
              type="button"
              onClick={
                isEditing
                  ? handleUpdate
                  : handleAdd
              }
              className="flex-1"
            >
              {isEditing
                ? "Update"
                : "Add"}
            </Button>

          </div>

        </div>

        {/* Validation error */}

        {error && (
          <div className="mt-3 text-sm text-destructive">
            {error}
          </div>
        )}

      </div>

      {/* =====================================================
          ITEMS TABLE
      ===================================================== */}

      {items.length > 0 && (
        <div className="mt-6 overflow-x-auto">

          <table className="w-full border-collapse">

            <thead>

              <tr className="border-b">

                <th className="px-4 py-3 text-left text-sm font-medium">
                  Application
                </th>

                <th className="px-4 py-3 text-left text-sm font-medium">
                  Quantity
                </th>

                <th className="px-4 py-3 text-right text-sm font-medium">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {items.map(
                (item, index) => (
                  <tr
                    key={`${item.application_id}-${index}`}
                    className="border-b last:border-0"
                  >

                    {/* Application */}

                    <td className="px-4 py-3">
                      {applicationLabel(
                        item.application_id,
                      )}
                    </td>

                    {/* Quantity */}

                    <td className="px-4 py-3">
                      {item.quantity}
                    </td>

                    {/* Actions */}

                    <td className="px-4 py-3">

                      <div className="flex justify-end gap-2">

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            handleEdit(index)
                          }
                        >
                          Edit
                        </Button>

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            handleDelete(index)
                          }
                        >
                          Delete
                        </Button>

                      </div>

                    </td>

                  </tr>
                ),
              )}

            </tbody>

          </table>

        </div>
      )}

    </FormCard>
  );
}